/** /decisions - pros/cons board for pending decisions, archive of
 *  made ones with rationale, new-decision form. Dotted until
 *  confirmed. */
import { useCallback, useEffect, useState } from "react";
import { Scale, Check, X, Plus } from "lucide-react";
import { api } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

type Decision = Record<string, unknown>;

export function Decisions() {
  const [items, setItems] = useState<Decision[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");

  const refresh = useCallback(() => {
    api.decisionsList().then((d) => setItems(d.decisions || [])).catch(() => undefined);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const act = async (id: string, action: string, body?: Record<string, unknown>) => {
    setBusy(id);
    try {
      await api.lifestyleAction("decisions", id, action, body);
      refresh();
    } finally {
      setBusy(null);
    }
  };

  const pending = items.filter((d) => d.status === "pending");
  const archive = items.filter((d) => d.status !== "pending");

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Explore", href: "/explore" }, { label: "Library", href: "/explore/library" }, { label: "Decisions" }]} />
      <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Decisions</h1>

      <TeachingBanner id="decisions_overview" />

      <form
        className="flex gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!newTitle.trim()) return;
          await api.decisionCreate(newTitle.trim(), "");
          setNewTitle("");
          refresh();
        }}
      >
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="New decision to weigh..."
          className="flex-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm"
        />
        <button type="submit" className="inline-flex items-center gap-1 rounded-lg bg-violet-600 text-white text-sm font-semibold px-3 py-1.5"><Plus className="w-4 h-4" /> Add</button>
      </form>

      <section className="space-y-2">
        <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500">Pending</h2>
        {pending.length === 0 && <p className="text-sm text-slate-500">Nothing pending.</p>}
        {pending.map((d) => {
          const id = String(d.id);
          const confirmed = Number(d.confirmed) === 1;
          const pros = (d.pros_json as string[]) || [];
          const cons = (d.cons_json as string[]) || [];
          return (
            <div
              key={id}
              className={`rounded-xl p-3 border ${
                confirmed
                  ? "border-slate-200/70 dark:border-slate-700/50"
                  : "border-dashed border-slate-400 dark:border-slate-600"
              } bg-white/60 dark:bg-slate-900/40`}
            >
              <div className="flex items-center gap-2">
                <Scale className="w-4 h-4 text-amber-500" />
                <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">{String(d.title)}</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 text-[12px]">
                <div>
                  <div className="font-bold text-emerald-700 dark:text-emerald-400 mb-1">Pros</div>
                  <ul className="list-disc pl-4 text-slate-600 dark:text-slate-300">{pros.map((p, i) => <li key={i}>{String(p)}</li>)}</ul>
                </div>
                <div>
                  <div className="font-bold text-rose-700 dark:text-rose-400 mb-1">Cons</div>
                  <ul className="list-disc pl-4 text-slate-600 dark:text-slate-300">{cons.map((c, i) => <li key={i}>{String(c)}</li>)}</ul>
                </div>
              </div>
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {!confirmed && (
                  <>
                    <button type="button" disabled={busy === id} onClick={() => act(id, "confirm")} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold px-2.5 py-1.5"><Check className="w-3.5 h-3.5" /> Confirm</button>
                    <button type="button" disabled={busy === id} onClick={() => act(id, "reject")} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5"><X className="w-3.5 h-3.5" /> Reject</button>
                  </>
                )}
                <button type="button" disabled={busy === id} onClick={() => act(id, "make", { final_choice: window.prompt("Final choice?") || "", rationale: window.prompt("Rationale?") || "" })} className="inline-flex items-center gap-1 rounded-lg bg-violet-600 text-white text-xs font-semibold px-2.5 py-1.5">Mark made</button>
                <button type="button" disabled={busy === id} onClick={() => act(id, "drop")} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5">Drop</button>
              </div>
            </div>
          );
        })}
      </section>

      {archive.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500">Archive</h2>
          {archive.map((d) => (
            <div key={String(d.id)} className="rounded-xl p-3 border border-slate-200/70 dark:border-slate-700/50 bg-white/40 dark:bg-slate-900/30 text-sm">
              <div className="font-semibold text-slate-900 dark:text-slate-100">{String(d.title)} <span className="text-[10px] uppercase text-slate-400">{String(d.status)}</span></div>
              {d.final_choice ? <div className="text-[12px] text-slate-600 dark:text-slate-300">Chose: {String(d.final_choice)}</div> : null}
              {d.rationale_md ? <div className="text-[11px] text-slate-500">{String(d.rationale_md)}</div> : null}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
